import { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { DeleteBrandDialog } from "@/components/shared/DeleteBrandDialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { TruncatedText } from "@/components/ui/truncated-text";
import { useAppData } from "@/contexts/AppDataContext";
import { memberApi } from "@/resources/member";
import { brandApi } from "@/resources/brand";
import { countryApi } from "@/resources/country";
import { accountApi } from "@/resources/account";
import { ACCOUNT_ROLE } from "@/constants";
import { EditBrandDialog } from "./brand-management/EditBrandDialog";
import { AddBrandDialog } from "./brand-management/AddBrandDialog";
import { PresetDialog } from "./brand-management/PresetDialog";
import { GroupPresetsCard } from "./brand-management/GroupPresetsCard";
import { getAssignedUserDisplayName } from "./constants";
export function BrandManagementTab() {
  const { brands, addBrand, brandGroupPresets, addBrandGroupPreset, updateBrandGroupPreset, deleteBrandGroupPreset } = useAppData();
  const { data: account } = accountApi.useGetAccount();
  const { data: membersData } = memberApi.useGetMembers();
  const { data: countries = [] } = countryApi.useGetCountries();
  const { mutate: assignBrandUsers, isPending: isAssigning } = brandApi.useAssignBrandUsers();
  const { mutate: updateBrand, isPending: isUpdating } = brandApi.useUpdateBrand();
  const canManage = account?.role === ACCOUNT_ROLE.OWNER || account?.role === ACCOUNT_ROLE.ADMIN;
  const [brandToDelete, setBrandToDelete] = useState<{ id: number; name: string } | null>(null);
  const [presetModalOpen, setPresetModalOpen] = useState(false);
  const [editingPreset, setEditingPreset] = useState<{ id: string; name: string; brandIds: string[] } | null>(null);
  const [presetName, setPresetName] = useState("");
  const [presetBrandIds, setPresetBrandIds] = useState<string[]>([]);
  const [presetToDelete, setPresetToDelete] = useState<{ id: string; name: string } | null>(null);

  const [editingBrand, setEditingBrand] = useState<{ id: number; name: string; countryId: number | null; products: number } | null>(null);
  const [editBrandName, setEditBrandName] = useState("");
  const [editBrandCountryId, setEditBrandCountryId] = useState<string>("");
  const [editAssignedUserIds, setEditAssignedUserIds] = useState<number[]>([]);

  const [addBrandModalOpen, setAddBrandModalOpen] = useState(false);
  const [newBrandName, setNewBrandName] = useState("");
  const [newBrandCountryId, setNewBrandCountryId] = useState<string>("");
  const [newBrandUserIds, setNewBrandUserIds] = useState<number[]>([]);

  const usCountryId = useMemo(() => {
    const us = countries.find((c) => c.amazon_country === "US");
    return us ? String(us.id) : "";
  }, [countries]);

  useEffect(() => {
    if (usCountryId && !newBrandCountryId) {
      setNewBrandCountryId(usCountryId);
    }
  }, [usCountryId]);

  const activeMembers = useMemo(() => {
    return (membersData?.members ?? []).filter((m) => m.status === "active" && m.type === "user");
  }, [membersData]);
  const handleAddBrand = () => {
    if (!newBrandName.trim()) return;
    addBrand({
      name: newBrandName.trim(),
      country_id: newBrandCountryId ? Number(newBrandCountryId) : null,
    }, () => {
      toast.success("Brand Created", { description: `"${newBrandName.trim()}" has been added.` });
      setAddBrandModalOpen(false);
      setNewBrandName("");
      setNewBrandCountryId(usCountryId);
      setNewBrandUserIds([]);
    });
  };
  const allBrands = useMemo(() => {
    const extras = [
      { id: "glossier", name: "Glossier" },
      { id: "thelabco", name: "The Lab Co." },
      { id: "kinfield", name: "Kinfield" },
      { id: "coola", name: "COOLA" },
    ];
    return [...brands, ...extras];
  }, [brands]);
  const openCreatePreset = () => {
    setEditingPreset(null);
    setPresetName("");
    setPresetBrandIds([]);
    setPresetModalOpen(true);
  };
  const openEditPreset = (preset: { id: string; name: string; brandIds: string[] }) => {
    setEditingPreset(preset);
    setPresetName(preset.name);
    setPresetBrandIds([...preset.brandIds]);
    setPresetModalOpen(true);
  };
  const handleSavePreset = () => {
    if (!presetName.trim()) return;
    if (editingPreset) {
      updateBrandGroupPreset(editingPreset.id, { name: presetName.trim(), brandIds: presetBrandIds });
      toast.success("Preset Updated", { description: `"${presetName.trim()}" has been updated.` });
    } else {
      addBrandGroupPreset({ name: presetName.trim(), brandIds: presetBrandIds });
      toast.success("Preset Created", { description: `"${presetName.trim()}" has been created.` });
    }
    setPresetModalOpen(false);
  };
  const handleDeletePreset = (id: string, name: string) => {
    setPresetToDelete({ id, name });
  };
  const confirmDeletePreset = () => {
    if (!presetToDelete) return;
    deleteBrandGroupPreset(presetToDelete.id);
    toast.success("Preset Deleted", { description: `"${presetToDelete.name}" has been removed.` });
    setPresetToDelete(null);
  };
  const togglePresetBrand = (brandId: string) => {
    setPresetBrandIds((prev) =>
      prev.includes(brandId) ? prev.filter((id) => id !== brandId) : [...prev, brandId]
    );
  };
  const toggleAssignedUser = (userId: number) => {
    setEditAssignedUserIds((prev) => prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]);
  };
  const allMembersSelected = activeMembers.length > 0 && activeMembers.every((m) => editAssignedUserIds.includes(m.id));
  const handleSelectAllMembers = () => {
    setEditAssignedUserIds(allMembersSelected ? [] : activeMembers.map((m) => m.id));
  };
  const toggleNewBrandUser = (userId: number) => {
    setNewBrandUserIds((prev) => prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]);
  };
  const allNewBrandMembersSelected = activeMembers.length > 0 && activeMembers.every((m) => newBrandUserIds.includes(m.id));
  const handleSelectAllNewBrandMembers = () => {
    setNewBrandUserIds(allNewBrandMembersSelected ? [] : activeMembers.map((m) => m.id));
  };
  const brandSettings = brands.map((b) => ({
    ...b,
    marketplace: b.marketplace ?? "Not set",
    products: b.products_count,
  }));
  const openEditBrand = (brand: typeof brandSettings[0]) => {
    setEditingBrand({
      id: brand.id,
      name: brand.name,
      countryId: brand.country_id,
      products: brand.products,
    });
    setEditBrandName(brand.name);
    setEditBrandCountryId(brand.country_id ? String(brand.country_id) : "");
    setEditAssignedUserIds(brand.assigned_users.map((u) => u.id));
  };
  const handleSaveBrand = () => {
    if (!editingBrand) return;

    if (!editBrandName.trim()) {
      toast.error("Validation Error", { description: "Brand name is required." });
      return;
    }

    if (editAssignedUserIds.length === 0) {
      toast.error("Validation Error", { description: "At least one user must be assigned to the brand." });
      return;
    }

    const countryId = editBrandCountryId ? Number(editBrandCountryId) : null;
    updateBrand(
      { brandId: editingBrand.id, name: editBrandName, country_id: countryId },
      {
        onSuccess: () => {
          assignBrandUsers(
            { brandId: editingBrand.id, user_ids: editAssignedUserIds },
            {
              onSuccess: () => {
                toast.success("Brand Updated", { description: `"${editBrandName}" has been saved.` });
                setEditingBrand(null);
              },
            }
          );
        },
      }
    );
  };
  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
        <Card className="flex flex-col">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">Brands</CardTitle>
                <CardDescription>Manage brands and their assigned users</CardDescription>
              </div>
              {canManage && (
                <Button size="sm" className="h-8 gap-1.5 text-xs" onClick={() => setAddBrandModalOpen(true)}>
                  <Plus className="h-3.5 w-3.5" />
                  Add Brand
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {brandSettings.map((brand) => (
              <div
                key={brand.id}
                onClick={() => canManage && openEditBrand(brand)}
                className={`p-4 rounded-lg border border-border hover:border-primary/20 transition-colors ${canManage ? 'cursor-pointer' : ''}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-sm font-semibold text-foreground">{brand.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{brand.marketplace} · {brand.products} products</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {canManage && <Button variant="outline" size="sm" className="h-7 text-xs rounded-lg bg-card" onClick={(e) => { e.stopPropagation(); openEditBrand(brand); }}>Edit</Button>}
                    {canManage && <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive" onClick={(e) => { e.stopPropagation(); setBrandToDelete({ id: brand.id, name: brand.name }); }}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>}
                  </div>
                </div>
                {canManage && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1.5">Assigned Users</p>
                    {brand.assigned_users.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {brand.assigned_users.slice(0, 4).map((user) => (
                          <Badge key={user.id} variant="secondary" className="text-xs border border-border bg-muted text-foreground font-normal max-w-[200px]">
                            <TruncatedText>{getAssignedUserDisplayName(user)}</TruncatedText>
                          </Badge>
                        ))}
                        {brand.assigned_users.length > 4 && (
                          <Badge variant="secondary" className="text-xs border border-border bg-muted/50 text-muted-foreground font-normal">
                            +{brand.assigned_users.length - 4}
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-muted-foreground">Unassigned</span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        <GroupPresetsCard
          presets={brandGroupPresets}
          allBrands={allBrands}
          onCreatePreset={openCreatePreset}
          onEditPreset={openEditPreset}
          presetToDelete={presetToDelete}
          onDeletePreset={handleDeletePreset}
          onPresetToDeleteChange={setPresetToDelete}
          onConfirmDeletePreset={confirmDeletePreset}
        />
      </div>

      <EditBrandDialog
        open={!!editingBrand}
        onOpenChange={() => setEditingBrand(null)}
        editBrandName={editBrandName}
        onEditBrandNameChange={setEditBrandName}
        editBrandCountryId={editBrandCountryId}
        onEditBrandCountryIdChange={setEditBrandCountryId}
        editAssignedUserIds={editAssignedUserIds}
        onToggleAssignedUser={toggleAssignedUser}
        onSelectAllMembers={handleSelectAllMembers}
        allMembersSelected={allMembersSelected}
        activeMembers={activeMembers}
        countries={countries}
        products={editingBrand?.products}
        onSave={handleSaveBrand}
        onCancel={() => setEditingBrand(null)}
        isUpdating={isUpdating}
        isAssigning={isAssigning}
      />

      <AddBrandDialog
        open={addBrandModalOpen}
        onOpenChange={setAddBrandModalOpen}
        newBrandName={newBrandName}
        onNewBrandNameChange={setNewBrandName}
        newBrandCountryId={newBrandCountryId}
        onNewBrandCountryIdChange={setNewBrandCountryId}
        countries={countries}
        activeMembers={activeMembers}
        assignedUserIds={newBrandUserIds}
        onToggleAssignedUser={toggleNewBrandUser}
        onSelectAllMembers={handleSelectAllNewBrandMembers}
        allMembersSelected={allNewBrandMembersSelected}
        onAdd={handleAddBrand}
      />

      <PresetDialog
        open={presetModalOpen}
        onOpenChange={setPresetModalOpen}
        editingPreset={editingPreset}
        presetName={presetName}
        onPresetNameChange={setPresetName}
        presetBrandIds={presetBrandIds}
        onToggleBrand={togglePresetBrand}
        allBrands={allBrands}
        brandGroupPresets={brandGroupPresets}
        onSave={handleSavePreset}
        onSetPresetName={setPresetName}
        onSetPresetBrandIds={setPresetBrandIds}
        presetToDelete={presetToDelete}
        onPresetToDeleteChange={setPresetToDelete}
        onConfirmDeletePreset={confirmDeletePreset}
      />

      <DeleteBrandDialog
        brand={brandToDelete}
        onCancel={() => setBrandToDelete(null)}
        onDeleted={() => setBrandToDelete(null)}
      />
    </>
  );
}
